import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../ui/command';
import { X, Plus } from 'lucide-react';
import InitialsAvatar from '../InitialsAvatar';

/**
 * Multi-talent picker for pitches. Empty selection = "full roster" (visible to
 * every talent), so the empty state reads that way rather than as an error.
 */
const PitchTalentPicker = ({ talent, selectedIds = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedTalents = useMemo(
    () => selectedIds.map((id) => talent.find((t) => t.user_id === id)).filter(Boolean),
    [selectedIds, talent]
  );
  const remaining = useMemo(() => talent.filter((t) => !selectedSet.has(t.user_id)), [talent, selectedSet]);

  const add = (id) => {
    if (selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
    setQuery('');
  };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div data-testid="pitch-talent-picker">
      {selectedTalents.length === 0 && (
        <div className="text-[11px] text-slate-400 italic mb-1.5" data-testid="pitch-talent-full-roster-hint">
          no one tagged yet — sits under "full roster", only visible to you
        </div>
      )}
      <div className="space-y-1.5">
        {selectedTalents.map((t) => (
          <div
            key={t.user_id}
            className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg"
            data-testid={`pitch-talent-chip-${t.user_id}`}
          >
            <InitialsAvatar name={t.name} size={24} />
            <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{t.name}</div>
            <button
              type="button"
              onClick={() => remove(t.user_id)}
              className="p-1 text-slate-400 hover:text-red-600"
              data-testid={`pitch-talent-remove-${t.user_id}`}
              title="remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-slate-300 rounded-lg text-xs text-slate-600 hover:border-pink-300 hover:text-pink-700"
            data-testid="pitch-talent-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> select roster
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-64" onClick={(e) => e.stopPropagation()}>
          <Command>
            <CommandInput value={query} onValueChange={setQuery} placeholder="search talent…" />
            <CommandList>
              <CommandEmpty>no talent match</CommandEmpty>
              <CommandGroup>
                {remaining.map((t) => (
                  <CommandItem key={t.user_id} onSelect={() => add(t.user_id)} value={`${t.name} ${t.email} ${t.handle || ''}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <InitialsAvatar name={t.name} size={22} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{t.name}</div>
                        <div className="text-[11px] text-slate-400 truncate">{t.handle || t.email}</div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PitchTalentPicker;
